import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchShopNumbering,
  updateShopNumbering,
  type ShopNumbering,
  type ShopNumberSequence,
  type UpdateShopNumberingResponse,
} from '../../lib/ops-numbering-api';
import { isShopAccessRequired, opsErrorMessage } from '../../lib/ops-api';
import {
  PREFIX_MAX_LENGTH,
  diffNumbering,
  draftFrom,
  isDraftDirty,
  needsReuseAcknowledgement,
  parseNext,
  previewNumber,
  toUpdatePayload,
  validateNumberingDraft,
  type NumberSequence,
  type NumberingChange,
  type NumberingDraft,
  type NumberingFieldErrors,
} from '../../lib/shop-numbering';
import { PageBody } from '../../components/Page';
import { StatusBanner } from '../../components/StatusBanner';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useShopFile } from '../../components/ShopFileLayout';
import { useLocale } from '../../i18n/LocaleProvider';

/** `1235` or `1235–1500`, each end formatted the way the document will be headed. */
function numberRange(
  first: number,
  last: number,
  format: (n: number) => string,
) {
  return first === last ? format(first) : `${format(first)} – ${format(last)}`;
}

/**
 * Editor for the numbering settings a merchant can set exactly once, in onboarding: the next
 * invoice / credit-note number, both prefixes and the year digits.
 *
 * Exists for shops that kept issuing invoices in their previous tool while switching over, whose
 * counter has to be moved past what that tool used — and to reserve a gap for a backfill. Every
 * change goes through a confirm dialog that spells out, per change, what it does to the
 * numbering; a counter moved DOWN additionally needs an explicit acknowledgement there. The
 * server re-checks every rule under a lock on the shop row, refuses a change if a document was
 * numbered since the tab loaded, and writes an `OperatorAuditLog` row with the change.
 *
 * The shop is the file's, and both calls carry its access grant — a refusal puts the reason
 * prompt back in front of the file.
 */
export function NumberingTab() {
  const { t, tNode } = useLocale();
  const { shopId, reload, requireAccess } = useShopFile();
  const queryClient = useQueryClient();
  // Lives here, not in the editor: a saved change re-keys the editor, which would drop it.
  const [saved, setSaved] = useState<UpdateShopNumberingResponse | null>(null);

  const numbering = useQuery({
    queryKey: ['shop-numbering', shopId],
    queryFn: () => fetchShopNumbering(shopId),
    retry: false,
  });

  useEffect(() => {
    if (numbering.isError && isShopAccessRequired(numbering.error, shopId)) {
      requireAccess();
    }
  }, [numbering.isError, numbering.error, shopId, requireAccess]);

  return (
    <PageBody narrow>
      <section className="card space-y-2">
        <h2 className="text-base font-semibold text-gray-900">
          {t('numbering.title')}
        </h2>
        <p className="text-sm text-gray-600">
          {tNode('numbering.lead', {
            auditLog: <code className="text-gray-900">OperatorAuditLog</code>,
          })}
        </p>
      </section>

      {numbering.isLoading && (
        <StatusBanner tone="loading">{t('common.loading')}</StatusBanner>
      )}

      {numbering.isError && (
        <StatusBanner tone="error">
          {opsErrorMessage(numbering.error, t('numbering.loadFailed'))}
        </StatusBanner>
      )}

      {saved && (
        <StatusBanner tone="success">
          <p className="font-semibold">
            {t('numbering.saved', {
              fields: saved.changedFields.join(', '),
            })}
          </p>
          <p className="text-xs font-mono break-all opacity-80 mt-1">
            {t('numbering.savedAudit', { id: saved.auditLogId })}
          </p>
        </StatusBanner>
      )}

      {numbering.data && (
        // Keyed on the state it was seeded from: a reload or a saved change re-seeds the
        // form from the server instead of keeping a draft typed against the old numbers.
        <NumberingEditor
          key={`${numbering.data.shopId}:${numbering.data.invoice.currentNo}:${numbering.data.creditNote.currentNo}:${numbering.data.invoice.prefix}:${numbering.data.creditNote.prefix}:${numbering.data.prependInvoiceYear}`}
          shopId={shopId}
          state={numbering.data}
          onSubmitStart={() => setSaved(null)}
          onAccessRequired={requireAccess}
          onSaved={(response) => {
            setSaved(response);
            queryClient.setQueryData(
              ['shop-numbering', shopId],
              response.numbering,
            );
            // The file's header flags an unconfigured numbering; this may just have fixed it.
            reload();
          }}
          onReload={() => numbering.refetch()}
          reloading={numbering.isFetching}
        />
      )}
    </PageBody>
  );
}

/** The form for one loaded shop, plus its confirm dialog. */
function NumberingEditor({
  shopId,
  state,
  onSubmitStart,
  onAccessRequired,
  onSaved,
  onReload,
  reloading,
}: {
  readonly shopId: string;
  readonly state: ShopNumbering;
  readonly onSubmitStart: () => void;
  readonly onAccessRequired: () => void;
  readonly onSaved: (response: UpdateShopNumberingResponse) => void;
  readonly onReload: () => void;
  readonly reloading: boolean;
}) {
  const { t, tNode } = useLocale();
  const [draft, setDraft] = useState<NumberingDraft>(() => draftFrom(state));
  const [reason, setReason] = useState('');
  const [fieldErrors, setFieldErrors] = useState<NumberingFieldErrors>({});
  // The submit is refused for a reason no single field can carry: the form differs from the
  // stored state but nothing in it actually moves a number.
  const [formError, setFormError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const changes = diffNumbering(state, draft);
  // Not `changes.length`: an input cleared or mistyped is no change, but the operator still has
  // to be told why nothing would happen — which only a submit (and its validation) does.
  const dirty = isDraftDirty(state, draft);
  const lowers = changes.some(needsReuseAcknowledgement);

  const save = useMutation({
    mutationFn: () =>
      updateShopNumbering(shopId, toUpdatePayload(state, changes, reason)),
    onSuccess: onSaved,
    onError: (error) => {
      if (isShopAccessRequired(error, shopId)) onAccessRequired();
    },
  });

  // A fresh dialog never inherits the acknowledgement given for a different set of changes.
  useEffect(() => {
    if (!confirming) setAcknowledged(false);
  }, [confirming]);

  function update<K extends keyof NumberingDraft>(
    key: K,
    value: NumberingDraft[K],
  ) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    save.reset();
    onSubmitStart();
    const errors = validateNumberingDraft(state, draft, reason);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setFormError('');
      return;
    }
    // Every field is valid, the form differs from the stored state, and still nothing moves —
    // a counter typed as `01235` where it already reads `1235`. Say so rather than no-op.
    if (changes.length === 0) {
      setFormError('numbering.errorNoChange');
      return;
    }
    setFormError('');
    setConfirming(true);
  }

  function performSave() {
    setConfirming(false);
    save.mutate();
  }

  /** The number as the next document would be headed, with the draft's prefix and year flag. */
  function formatFor(sequence: NumberSequence, value: number): string {
    const prefix =
      sequence === 'invoice' ? draft.invoicePrefix : draft.creditNotePrefix;
    return previewNumber(
      prefix,
      draft.prependInvoiceYear,
      state.yearDigitsToday,
      value,
    );
  }

  const sequences: Array<{
    sequence: NumberSequence;
    data: ShopNumberSequence;
    nextField: 'nextInvoiceNo' | 'nextCreditNoteNo';
    prefixField: 'invoicePrefix' | 'creditNotePrefix';
  }> = [
    {
      sequence: 'invoice',
      data: state.invoice,
      nextField: 'nextInvoiceNo',
      prefixField: 'invoicePrefix',
    },
    {
      sequence: 'creditNote',
      data: state.creditNote,
      nextField: 'nextCreditNoteNo',
      prefixField: 'creditNotePrefix',
    },
  ];

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-6">
        {sequences.map(({ sequence, data, nextField, prefixField }) => {
          const nextValue = parseNext(draft[nextField]);
          return (
            <div key={sequence} className="card space-y-5">
              <h2 className="text-base font-semibold text-gray-900">
                {t(
                  sequence === 'invoice'
                    ? 'numbering.invoiceTitle'
                    : 'numbering.creditNoteTitle',
                )}
              </h2>

              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <Fact label={t('numbering.factStart')}>
                  {data.startNo ?? '—'}
                </Fact>
                <Fact label={t('numbering.factIssued')}>
                  {data.issuedCount}
                </Fact>
                <Fact label={t('numbering.factHighest')}>
                  {data.highestIssuedFormatted ?? '—'}
                </Fact>
                <Fact label={t('numbering.factNext')}>
                  {data.nextFormatted ?? '—'}
                </Fact>
              </dl>

              {!data.configured ? (
                <StatusBanner tone="info">
                  {t('numbering.notConfigured')}
                </StatusBanner>
              ) : (
                <>
                  <div>
                    <label htmlFor={nextField} className="field-label">
                      {t('numbering.nextLabel')}
                    </label>
                    <input
                      id={nextField}
                      type="number"
                      min={data.minNextNo}
                      step="1"
                      value={draft[nextField]}
                      onChange={(e) => update(nextField, e.target.value)}
                      autoComplete="off"
                      className="field-input font-mono"
                    />
                    {fieldErrors[nextField] && (
                      <p className="field-error">
                        {t(fieldErrors[nextField], { min: data.minNextNo })}
                      </p>
                    )}
                    <p className="field-hint">
                      {t('numbering.nextHint', { min: data.minNextNo })}
                    </p>
                    {nextValue != null && (
                      <p className="field-hint">
                        {tNode('numbering.nextPreview', {
                          number: (
                            <code className="text-gray-900">
                              {formatFor(sequence, nextValue)}
                            </code>
                          ),
                        })}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor={prefixField} className="field-label">
                      {t('numbering.prefixLabel')}
                    </label>
                    <input
                      id={prefixField}
                      type="text"
                      value={draft[prefixField]}
                      onChange={(e) => update(prefixField, e.target.value)}
                      maxLength={PREFIX_MAX_LENGTH}
                      disabled={data.prefixLocked}
                      autoComplete="off"
                      className="field-input font-mono"
                    />
                    {fieldErrors[prefixField] && (
                      <p className="field-error">
                        {t(fieldErrors[prefixField])}
                      </p>
                    )}
                    <p className="field-hint">
                      {t(
                        data.prefixLocked
                          ? 'numbering.prefixLockedHint'
                          : 'numbering.prefixHint',
                      )}
                    </p>
                  </div>
                </>
              )}
            </div>
          );
        })}

        <div className="card space-y-2">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={draft.prependInvoiceYear}
              onChange={(e) => update('prependInvoiceYear', e.target.checked)}
              disabled={state.prependYearLocked}
              className="w-4 h-4 rounded border-gray-300 accent-primary"
            />
            {t('numbering.yearLabel', { digits: state.yearDigitsToday })}
          </label>
          <p className="field-hint">
            {t(
              state.prependYearLocked
                ? 'numbering.yearLockedHint'
                : 'numbering.yearHint',
            )}
          </p>
        </div>

        <div className="card space-y-5">
          <div>
            <label htmlFor="numberingReason" className="field-label">
              {t('backfill.reason')}
            </label>
            <textarea
              id="numberingReason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder={t('numbering.reasonPlaceholder')}
              className="field-input"
            />
            <div className="flex justify-between items-center gap-4">
              {fieldErrors.reason ? (
                <p className="field-error">{t(fieldErrors.reason)}</p>
              ) : (
                <p className="field-hint">{t('backfill.reasonHint')}</p>
              )}
              <p className="field-hint tabular-nums shrink-0">
                {reason.length}/500
              </p>
            </div>
          </div>

          {save.isError && (
            <StatusBanner tone="error">
              <p>{opsErrorMessage(save.error, t('numbering.saveFailed'))}</p>
              <button
                type="button"
                onClick={onReload}
                disabled={reloading}
                className="btn-neutral btn-sm mt-2"
              >
                {t('numbering.reload')}
              </button>
            </StatusBanner>
          )}

          {formError && <p className="field-error">{t(formError)}</p>}

          <button
            type="submit"
            disabled={!dirty || save.isPending}
            className="btn-danger btn-sm w-full"
          >
            {save.isPending
              ? t('numbering.submitting')
              : t('numbering.submit', { count: changes.length })}
          </button>
        </div>
      </form>

      <ConfirmDialog
        open={confirming}
        title={t('numbering.confirmTitle')}
        confirmLabel={t('numbering.confirmLabel')}
        confirmDisabled={lowers && !acknowledged}
        onConfirm={performSave}
        onCancel={() => setConfirming(false)}
      >
        <p>
          {tNode('numbering.confirmIntro', {
            shop: <code className="text-gray-900">{state.shopDomain}</code>,
          })}
        </p>
        <ul className="list-disc pl-5 space-y-2 mt-3">
          {changes.map((change) => (
            <li
              key={`${change.kind}:${'sequence' in change ? change.sequence : ''}`}
            >
              <ChangeLine change={change} formatFor={formatFor} />
            </li>
          ))}
        </ul>
        {lowers && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-red-800">
            <p className="font-medium">{t('numbering.lowerWarning')}</p>
            <label className="flex items-start gap-2 mt-2">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                className="w-4 h-4 mt-0.5 rounded border-gray-300 accent-red-600"
              />
              {t('numbering.lowerAcknowledge')}
            </label>
          </div>
        )}
        <p className="mt-4">{t('numbering.confirmFooter')}</p>
      </ConfirmDialog>
    </>
  );
}

/** One label/value pair of a sequence's facts. */
function Fact({
  label,
  children,
}: {
  readonly label: string;
  readonly children: ReactNode;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="font-mono text-gray-900 break-all">{children}</dd>
    </div>
  );
}

/** One change in the confirm dialog: what moves, and what that means for the numbering. */
function ChangeLine({
  change,
  formatFor,
}: {
  readonly change: NumberingChange;
  readonly formatFor: (sequence: NumberSequence, value: number) => string;
}) {
  const { t, tNode } = useLocale();
  const code = (value: string) => (
    <code className="text-gray-900">{value || '—'}</code>
  );

  if (change.kind === 'counter') {
    const format = (value: number) => formatFor(change.sequence, value);
    const range =
      change.direction === 'raise'
        ? numberRange(change.fromNext, change.toNext - 1, format)
        : numberRange(change.toNext, change.fromNext - 1, format);
    return (
      <>
        {tNode(
          change.sequence === 'invoice'
            ? 'numbering.changeInvoiceCounter'
            : 'numbering.changeCreditNoteCounter',
          {
            from: code(format(change.fromNext)),
            to: code(format(change.toNext)),
          },
        )}{' '}
        {change.emptySequence ? (
          tNode('numbering.consequenceEmpty', {
            to: code(format(change.toNext)),
          })
        ) : (
          <span className={change.direction === 'lower' ? 'text-red-700' : ''}>
            {tNode(
              change.direction === 'raise'
                ? 'numbering.consequenceRaise'
                : 'numbering.consequenceLower',
              { range: code(range) },
            )}
          </span>
        )}
      </>
    );
  }

  if (change.kind === 'prefix') {
    return (
      <>
        {tNode(
          change.sequence === 'invoice'
            ? 'numbering.changeInvoicePrefix'
            : 'numbering.changeCreditNotePrefix',
          { from: code(change.from), to: code(change.to) },
        )}{' '}
        {t('numbering.consequencePrefix')}
      </>
    );
  }

  return (
    <>
      {t(change.to ? 'numbering.changeYearOn' : 'numbering.changeYearOff')}{' '}
      {t('numbering.consequenceYear')}
    </>
  );
}
